import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useI18n, LANGUAGES } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { switchLanguagePath, resolvePublicRoute } from '../i18n/routes';
import { Globe } from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Language switcher with two behaviours:
 *   context="public"  → navigates to the sibling localized URL
 *   (default)         → only updates I18n context state (for authenticated areas)
 *
 * If the user is authenticated, the preference is also persisted to their profile.
 */
export default function LanguageSwitcher({ variant = 'light', context }) {
  const { lang, setLang } = useI18n();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = LANGUAGES.find(l => l.code === lang);
  const isDark = variant === 'dark';

  const isPublicPage = context === 'public' || !!resolvePublicRoute(pathname);

  const handleSwitch = (code) => {
    if (isPublicPage) {
      // Navigate to the equivalent page in the target language
      const target = switchLanguagePath(pathname, code);
      navigate(target);
    }
    setLang(code);
    setOpen(false);
    // Persist to backend profile if authenticated
    if (isAuthenticated) {
      axios.put(`${API}/api/investor/language`, { preferred_language: code }, { withCredentials: true }).catch(() => {});
    }
  };

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition duration-200 border ${
          isDark
            ? 'border-ivory/20 text-ivory/80 hover:text-ivory hover:border-ivory/40'
            : 'border-stone text-graphite hover:text-charcoal hover:border-bronze/40'
        }`}
        data-testid="language-switcher-btn"
      >
        <Globe size={14} weight="regular" />
        <span className="uppercase tracking-wider">{currentLang?.flag}</span>
      </button>

      {open && (
        <div className={`absolute right-0 top-full mt-1.5 z-50 min-w-[120px] border shadow-elevated ${
          isDark ? 'bg-charcoal border-ivory/20' : 'bg-white border-stone'
        }`} data-testid="language-dropdown">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => handleSwitch(l.code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                lang === l.code
                  ? isDark ? 'bg-ivory/10 text-ivory font-medium' : 'bg-stone/50 text-charcoal font-medium'
                  : isDark ? 'text-ivory/70 hover:bg-ivory/5 hover:text-ivory' : 'text-graphite hover:bg-stone/30 hover:text-charcoal'
              }`}
              data-testid={`lang-option-${l.code}`}
            >
              <span className="text-xs uppercase tracking-wider font-medium w-5">{l.flag}</span>
              <span>{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
